import { CheckIcon, XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useBlocker } from 'react-router-dom';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

import type { BulkDonorScheduleState } from '../hooks/use-bulk-donor-schedule-store';

type UnsavedChangesGuardProps<Row extends object> = {
  state: BulkDonorScheduleState<Row>;
};

export function UnsavedChangesGuard<Row extends object>({
  state,
}: UnsavedChangesGuardProps<Row>) {
  const { t } = useTranslation();
  const hasUnsavedChanges =
    state.type !== 'success' &&
    state.rows.some((row) =>
      Object.values(row ?? {}).some(
        (value) =>
          value !== null &&
          value !== undefined &&
          (typeof value !== 'string' || value.trim() !== ''),
      ),
    );
  const blocker = useBlocker(hasUnsavedChanges);

  return (
    <AlertDialog
      open={blocker.state === 'blocked'}
      onOpenChange={(open) => {
        if (!open && blocker.state === 'blocked') {
          blocker.reset();
        }
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('common.warning')}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('common.unsavedChanges')} {t('common.doYouWantToProceed')}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>
            <XIcon />
            {t('common.no')}
          </AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            onClick={() => {
              if (blocker.state === 'blocked') {
                blocker.proceed();
              }
            }}
          >
            <CheckIcon />
            {t('common.yes')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
